import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ring-offset-background",
  {
    variants: {
      variant: {
        success: "border-2 border-brand-success bg-brand-success text-white font-semibold hover:bg-brand-success/90",
        successOutline: "border-2 border-brand-success bg-transparent text-brand-success font-semibold hover:bg-brand-success/10",
        destructiveOutline: "border-2 border-destructive bg-transparent text-destructive font-semibold hover:bg-destructive/10",
        default: "btn-primary-adaptive border-2 border-[var(--color-navy)] bg-[var(--color-navy)] text-[var(--color-orange)] font-semibold hover:bg-[var(--color-navy-mid)]",
        destructive: "btn-destructive-adaptive border-2 border-destructive bg-destructive text-destructive-foreground font-semibold hover:bg-destructive/90",
        outline: "border-2 border-foreground/30 bg-transparent text-foreground hover:bg-foreground/5 hover:border-foreground/50",
        secondary: "border-2 border-foreground/30 bg-transparent text-foreground hover:bg-foreground/5 hover:border-foreground/50",
        ghost: "hover:bg-foreground/5 hover:text-foreground",
        link: "text-[var(--color-orange)] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp data-slot="button" data-variant={variant ?? "default"} className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
